import { useCallback, useEffect, useMemo, useState } from 'react';
import { GlobalFontServices } from './GlobalFontServices';

export const BUILT_IN_FONT_OPTIONS = [
    { value: '', label: 'Default' },
    { value: 'Roboto', label: 'Roboto' },
    { value: 'Inter', label: 'Inter' },
    { value: 'Poppins', label: 'Poppins' },
    { value: 'Lato', label: 'Lato' },
    { value: 'Open Sans', label: 'Open Sans' },
    { value: 'Montserrat', label: 'Montserrat' },
    { value: 'Nunito', label: 'Nunito' },
    { value: 'Raleway', label: 'Raleway' },
    { value: 'Playfair Display', label: 'Playfair Display' }
];

let cachedFonts = null;
let activeRequest = null;
const subscribers = new Set();

const publishFonts = (fonts) => {
    subscribers.forEach(listener => listener(fonts));
};

const sanitizeCssValue = (value) => (value || '').toString().replace(/["'\\\n\r{};]/g, '').trim();

export const buildGlobalFontFaceCss = (fonts = []) => fonts.map(font => {
    const family = sanitizeCssValue(font.name);
    const url = sanitizeCssValue(font.fileUrl);
    const format = {
        ttf: 'truetype',
        otf: 'opentype',
        woff: 'woff',
        woff2: 'woff2'
    }[sanitizeCssValue(font.fileFormat).toLowerCase()];
    return family && url
        ? `@font-face { font-family: '${family}'; src: url('${url}')${format ? ` format('${format}')` : ''}; font-style: normal; font-display: swap; }`
        : '';
}).filter(Boolean).join('\n');

const fetchActiveFonts = async (force = false) => {
    if (!force && cachedFonts) return cachedFonts;
    if (!force && activeRequest) return activeRequest;
    activeRequest = GlobalFontServices.listActive()
        .then(response => {
            const succeeded = response?.success === 1 || response?.success === true;
            const nextFonts = succeeded ? response.data || [] : [];
            // Do not permanently cache an API failure as an empty catalog. A
            // later form mount can retry after the backend/network recovers.
            cachedFonts = succeeded ? nextFonts : null;
            publishFonts(nextFonts);
            return nextFonts;
        })
        .finally(() => {
            activeRequest = null;
        });
    return activeRequest;
};

export const invalidateGlobalFontCache = () => {
    cachedFonts = null;
    activeRequest = null;
};

export const refreshGlobalFontCache = async () => {
    invalidateGlobalFontCache();
    return fetchActiveFonts(true);
};

export const useGlobalFonts = () => {
    const [fonts, setFonts] = useState(cachedFonts || []);
    const [loading, setLoading] = useState(!cachedFonts);

    const refresh = useCallback(async () => {
        setLoading(true);
        const nextFonts = await refreshGlobalFontCache();
        setLoading(false);
        return nextFonts;
    }, []);

    useEffect(() => {
        let mounted = true;
        const handleFontsChanged = nextFonts => {
            if (mounted) {
                setFonts(nextFonts);
                setLoading(false);
            }
        };
        subscribers.add(handleFontsChanged);
        fetchActiveFonts().then(nextFonts => {
            if (mounted) {
                setFonts(nextFonts);
                setLoading(false);
            }
        });
        return () => {
            mounted = false;
            subscribers.delete(handleFontsChanged);
        };
    }, []);

    const options = useMemo(() => [
        ...BUILT_IN_FONT_OPTIONS,
        ...fonts.map(font => ({ value: font.name, label: font.name, font }))
    ].filter((option, index, all) => (
        all.findIndex(item => item.value.toLowerCase() === option.value.toLowerCase()) === index
    )), [fonts]);

    return {
        fonts,
        options,
        fontFaceCss: useMemo(() => buildGlobalFontFaceCss(fonts), [fonts]),
        loading,
        refresh
    };
};

export const GlobalFontStyles = () => {
    const { fontFaceCss } = useGlobalFonts();
    return fontFaceCss ? <style>{fontFaceCss}</style> : null;
};
